import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { UserWidgetConfigUpdateStore } from "../../store";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { UserCreate } from "../../../user/components/create/UserCreate";
import { UserCreateStore } from "../../../user/store";
import { InputBoolean } from "../../../common/controls";
import { InputCodeEditor } from "../../../common/controls";
import { InputNumber } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type UserWidgetConfigUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  userSelectedValue?: { displayId: string; id: string } | null;
  onUserChange?: (value: string) => void;
  onUserSelected?: (value: { displayId: string; id: string } | null) => void;
  userOptions?: { displayId: string; id: string }[];
  userError?: string;
  widgetSelectedValue?: { displayId: string; id: string } | null;
  onWidgetChange?: (value: string) => void;
  onWidgetSelected?: (value: { displayId: string; id: string } | null) => void;
  widgetOptions?: { displayId: string; id: string }[];
  widgetError?: string;
  enabled: boolean;
  enabledSelectedValue?: { displayId: string; id: string } | null;
  onEnabledChange?: (value: boolean) => void;
  enabledError?: string;
  position: string;
  positionSelectedValue?: { displayId: string; id: string } | null;
  onPositionChange?: (value: string) => void;
  positionError?: string;
  configOverride: string;
  configOverrideSelectedValue?: { displayId: string; id: string } | null;
  onConfigOverrideChange?: (value: string) => void;
  configOverrideError?: string;
  displayOrder: number | undefined;
  displayOrderSelectedValue?: { displayId: string; id: string } | null;
  onDisplayOrderChange?: (value: number | undefined) => void;
  displayOrderError?: string;
  userWidgetConfigUpdateStore: UserWidgetConfigUpdateStore;
};

export const UserWidgetConfigUpdateForm: React.FC<UserWidgetConfigUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      onUserSelected = () => {},
      userOptions = [],
      userSelectedValue = null,
      userError,
      onUserChange = () => {},
      onWidgetSelected = () => {},
      widgetOptions = [],
      widgetSelectedValue = null,
      widgetError,
      onWidgetChange = () => {},
      enabled = false,
      enabledError,
      onEnabledChange = () => {},
      position = "{}",
      positionError,
      onPositionChange = () => {},
      configOverride = "{}",
      configOverrideError,
      onConfigOverrideChange = () => {},
      displayOrder = undefined,
      displayOrderError,
      onDisplayOrderChange = () => {},
      userWidgetConfigUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="UserWidgetConfig"
              mode="update"
              values={{}}
              options={{ user: userOptions, widget: widgetOptions }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "user":
                    onUserSelected?.(valor);
                    break;
                  case "widget":
                    onWidgetSelected?.(valor);
                    break;
                  case "enabled":
                    onEnabledChange?.(valor);
                    break;
                  case "position":
                    onPositionChange?.(valor);
                    break;
                  case "configOverride":
                    onConfigOverrideChange?.(valor);
                    break;
                  case "displayOrder":
                    onDisplayOrderChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={userWidgetConfigUpdateStore.getCardErrors("null")}
              title={langStore.groupUserWidgetConfigLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputReference
                  id="user"
                  label={langStore.UserWidgetConfigUserLabel}
                  autoFocus
                  error={Boolean(userError)}
                  helperText={
                    userError ||
                    userWidgetConfigUpdateStore.warnings["user"]?.message
                  }
                  selectedValue={userSelectedValue}
                  onChange={(value: string) => onUserChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onUserSelected(value)
                  }
                  options={userOptions}
                  description={langStore.UserWidgetConfigUserDescription}
                  canCreate={rootStore.authStore.hasPermissions("user_create")}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new UserCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <UserCreate
                          userCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewUser"
                      cancelMethod="cancelNewUser"
                    />
                  )}
                />
                <InputReference
                  id="widget"
                  label={langStore.UserWidgetConfigWidgetLabel}
                  error={Boolean(widgetError)}
                  helperText={
                    widgetError ||
                    userWidgetConfigUpdateStore.warnings["widget"]?.message
                  }
                  selectedValue={widgetSelectedValue}
                  onChange={(value: string) => onWidgetChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onWidgetSelected(value)
                  }
                  options={widgetOptions}
                  description={langStore.UserWidgetConfigWidgetDescription}
                  canCreate={false}
                />
                <InputBoolean
                  value={enabled}
                  error={Boolean(enabledError)}
                  helperText={
                    enabledError ||
                    userWidgetConfigUpdateStore.warnings["enabled"]?.message
                  }
                  label={langStore.UserWidgetConfigEnabledLabel}
                  description={langStore.UserWidgetConfigEnabledDescription}
                  onChange={(value: boolean) => onEnabledChange(value)}
                />
                <InputCodeEditor
                  value={position}
                  error={Boolean(positionError)}
                  helperText={
                    positionError ||
                    userWidgetConfigUpdateStore.warnings["position"]?.message
                  }
                  label={langStore.UserWidgetConfigPositionLabel}
                  onChange={(value: string) => onPositionChange(value)}
                  language="json"
                  description={langStore.UserWidgetConfigPositionDescription}
                />
                <InputCodeEditor
                  value={configOverride}
                  error={Boolean(configOverrideError)}
                  helperText={
                    configOverrideError ||
                    userWidgetConfigUpdateStore.warnings["configOverride"]
                      ?.message
                  }
                  label={langStore.UserWidgetConfigConfigOverrideLabel}
                  onChange={(value: string) => onConfigOverrideChange(value)}
                  language="json"
                  description={
                    langStore.UserWidgetConfigConfigOverrideDescription
                  }
                />
                <InputNumber
                  value={displayOrder}
                  nullable={true}
                  error={Boolean(displayOrderError)}
                  warning={
                    !displayOrderError &&
                    Boolean(
                      userWidgetConfigUpdateStore.warnings["displayOrder"]
                    )
                  }
                  helperText={
                    displayOrderError ||
                    userWidgetConfigUpdateStore.warnings["displayOrder"]
                      ?.message
                  }
                  label={langStore.UserWidgetConfigDisplayOrderLabel}
                  decimalScale={0}
                  nullText={langStore.optionalFieldHint}
                  description={
                    langStore.UserWidgetConfigDisplayOrderDescription
                  }
                  onChange={(value: number | undefined) =>
                    onDisplayOrderChange(value)
                  }
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
